import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Expenses.module.css';
import { ChevronRight, Plus, X, Upload, Camera, Trash, Eye, Check } from 'lucide-react';

const ExpenseForm = ({
  expense,
  newExpense,
  isEditing,
  categories,
  expenseTypes,
  paymentMethods,
  checks,
  setChecks,
  validationErrors,
  setValidationErrors,
  loading,
  error,
  isUploading,
  fileInputRef,
  cameraInputRef,
  onExpenseInputChange,
  onPaymentMethodChange,
  onFileUpload,
  onCameraCapture,
  onRemoveUploadedFile,
  onSave,
  onCancel,
  formatFileSize,
  onViewInvoice
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onCancel]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentExpense = isEditing ? expense : newExpense;

  // Mobile update button (rendered via portal)
  const mobileUpdateButton = isMobile && isEditing && createPortal(
    <button
      type="button"
      className={`${styles.saveButton} ${styles.updateButton} ${styles.mobileFloatingButton}`}
      onClick={(e) => {
        e.preventDefault();
        onSave(e);
      }}
      disabled={loading}
    >
      <Check size={20} />
    </button>,
    document.body
  );

  // Mobile cancel button (rendered via portal)
  const mobileCancelButton = isMobile && createPortal(
    <button
      type="button"
      className={`${styles.cancelButton} ${styles.mobileCancelButton}`}
      onClick={onCancel}
      disabled={loading}
    >
      <X size={20} />
    </button>,
    document.body
  );

  // Mobile header (rendered via portal)
  const mobileHeader = isMobile && createPortal(
    <div className={`${styles.formHeader} ${styles.mobileFormHeader}`}>
      <button
        className={styles.backButton}
        onClick={onCancel}
        type="button"
        aria-label="חזור"
      >
        <ChevronRight size={20} />
      </button>
      <h2>{isEditing ? 'עריכת הוצאה' : 'הוספת הוצאה חדשה'}</h2>
    </div>,
    document.body
  );

  return (
    <>
    {mobileHeader}
    <div className={styles.expenseFormContainer}>
      {/* Form Header - Hidden on mobile (rendered via portal) */}
      {!isMobile && (
        <div className={styles.formHeader}>
          <button
            className={styles.backButton}
            onClick={onCancel}
            type="button"
            aria-label="חזור"
          >
            <ChevronRight size={20} />
          </button>
          <h2>{isEditing ? 'עריכת הוצאה' : 'הוספת הוצאה חדשה'}</h2>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}

      {/* Form Body */}
      <form className={styles.expenseForm} onSubmit={(e) => { e.preventDefault(); onSave(); }}>
        {/* Main Fields Section */}
        <div className={styles.formSection}>
          <div className={styles.formRow}>
            {/* Date Field */}
            <div className={styles.formGroup}>
              <label htmlFor="expenseDate">תאריך: *</label>
              <input
                id="expenseDate"
                type="date"
                name="date"
                value={currentExpense.date || ''}
                onChange={onExpenseInputChange}
                disabled={currentExpense.paymentMethod === "צ'ק"}
                required
                className={validationErrors.date ? styles.error : ''}
                aria-describedby={validationErrors.date ? "date-error" : undefined}
              />
              {validationErrors.date && (
                <div id="date-error" className={styles.validationError} role="alert">
                  {validationErrors.date}
                </div>
              )}
            </div>

            {/* Description Field */}
            <div className={styles.formGroup}>
              <label htmlFor="expenseDescription">תיאור: *</label>
              <input
                id="expenseDescription"
                type="text"
                name="description"
                value={currentExpense.description || ''}
                onChange={onExpenseInputChange}
                placeholder="תאר את ההוצאה"
                required
                className={validationErrors.description ? styles.error : ''}
                aria-describedby={validationErrors.description ? "description-error" : undefined}
              />
              {validationErrors.description && (
                <div id="description-error" className={styles.validationError} role="alert">
                  {validationErrors.description}
                </div>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            {/* Amount Field */}
            <div className={styles.formGroup}>
              <label htmlFor="expenseAmount">סכום (₪): *</label>
              <input
                id="expenseAmount"
                type="number"
                name="amount"
                value={currentExpense.amount || ''}
                onChange={onExpenseInputChange}
                placeholder="0.00"
                required
                className={validationErrors.amount ? styles.error : ''}
                aria-describedby={validationErrors.amount ? "amount-error" : undefined}
              />
              {validationErrors.amount && (
                <div id="amount-error" className={styles.validationError} role="alert">
                  {validationErrors.amount}
                </div>
              )}
            </div>

            {/* Category Field */}
            <div className={styles.formGroup}>
              <label htmlFor="expenseCategory">קטגוריה:</label>
              <select
                id="expenseCategory"
                name="category"
                value={currentExpense.category || 'תחזוקה שוטפת'}
                onChange={onExpenseInputChange}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formRow}>
            {/* Expense Type Field */}
            <div className={styles.formGroup}>
              <label htmlFor="expenseType">סוג הוצאה:</label>
              <select
                id="expenseType"
                name="expenseType"
                value={currentExpense.expenseType || 'משתנה'}
                onChange={onExpenseInputChange}
              >
                {expenseTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Payment Method Field */}
            <div className={styles.formGroup}>
              <label htmlFor="paymentMethod">אמצעי תשלום:</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={currentExpense.paymentMethod || 'מזומן'}
                onChange={onPaymentMethodChange}
              >
                {paymentMethods.map(method => (
                  <option
                    key={method}
                    value={method}
                    disabled={method === "צ'ק" && !currentExpense.date}
                  >
                    {method === "צ'ק" && !currentExpense.date ? "צ'ק (נדרש תאריך)" : method}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Check Payment Fields */}
        {currentExpense.paymentMethod === "צ'ק" && (
          <div className={styles.formSection}>
            <div className={styles.checkHeader}>
              <h4>פרטי צ'קים</h4>
              <button
                type="button"
                className={styles.addCheckButton}
                onClick={() => {
                  const hasEmptyChecks = checks.some(check =>
                    !check.checkNumber || !check.checkMaturityDate || !check.amount
                  );

                  if (hasEmptyChecks) {
                    alert('אנא מלא את כל פרטי הצ\'קים הקיימים לפני הוספת צ\'ק חדש');
                    return;
                  }

                  let newDate = '';
                  if (checks.length > 0 && checks[checks.length - 1].checkMaturityDate) {
                    const lastDate = new Date(checks[checks.length - 1].checkMaturityDate);
                    lastDate.setMonth(lastDate.getMonth() + 1);
                    newDate = lastDate.toISOString().split('T')[0];
                  }

                  setChecks([...checks, { checkNumber: '', checkMaturityDate: newDate, amount: '' }]);
                }}
              >
                <Plus size={16} />
                הוסף צ'ק
              </button>
            </div>

            {checks.map((check, index) => (
              <div key={index} className={styles.checkRow}>
                <div className={styles.checkFields}>
                  <div className={styles.formGroup}>
                    <label htmlFor={`checkNumber-${index}`}>מספר צ'ק: *</label>
                    <input
                      id={`checkNumber-${index}`}
                      type="text"
                      value={check.checkNumber}
                      onChange={(e) => {
                        const newChecks = [...checks];
                        newChecks[index].checkNumber = e.target.value;
                        setChecks(newChecks);

                        if (validationErrors.checks?.[`check_${index}`]?.checkNumber) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            if (newErrors.checks?.[`check_${index}`]) {
                              delete newErrors.checks[`check_${index}`].checkNumber;
                              if (Object.keys(newErrors.checks[`check_${index}`]).length === 0) {
                                delete newErrors.checks[`check_${index}`];
                              }
                            }
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="מספר צ'ק"
                      required
                      className={validationErrors.checks?.[`check_${index}`]?.checkNumber ? styles.invalid : ''}
                    />
                    {validationErrors.checks?.[`check_${index}`]?.checkNumber && (
                      <div className={styles.validationError}>
                        {validationErrors.checks[`check_${index}`].checkNumber}
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor={`checkMaturityDate-${index}`}>תאריך פירעון: *</label>
                    <input
                      id={`checkMaturityDate-${index}`}
                      type="date"
                      value={check.checkMaturityDate}
                      onChange={(e) => {
                        const newChecks = [...checks];
                        newChecks[index].checkMaturityDate = e.target.value;
                        setChecks(newChecks);

                        if (validationErrors.checks?.[`check_${index}`]?.checkMaturityDate) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            if (newErrors.checks?.[`check_${index}`]) {
                              delete newErrors.checks[`check_${index}`].checkMaturityDate;
                              if (Object.keys(newErrors.checks[`check_${index}`]).length === 0) {
                                delete newErrors.checks[`check_${index}`];
                              }
                            }
                            return newErrors;
                          });
                        }
                      }}
                      required
                      className={validationErrors.checks?.[`check_${index}`]?.checkMaturityDate ? styles.invalid : ''}
                    />
                    {validationErrors.checks?.[`check_${index}`]?.checkMaturityDate && (
                      <div className={styles.validationError}>
                        {validationErrors.checks[`check_${index}`].checkMaturityDate}
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor={`checkAmount-${index}`}>סכום (₪): *</label>
                    <input
                      id={`checkAmount-${index}`}
                      type="number"
                      value={check.amount}
                      onChange={(e) => {
                        const newChecks = [...checks];
                        newChecks[index].amount = e.target.value;
                        setChecks(newChecks);

                        if (validationErrors.checks?.[`check_${index}`]?.amount) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            if (newErrors.checks?.[`check_${index}`]) {
                              delete newErrors.checks[`check_${index}`].amount;
                              if (Object.keys(newErrors.checks[`check_${index}`]).length === 0) {
                                delete newErrors.checks[`check_${index}`];
                              }
                            }
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                      className={validationErrors.checks?.[`check_${index}`]?.amount ? styles.invalid : ''}
                    />
                    {validationErrors.checks?.[`check_${index}`]?.amount && (
                      <div className={styles.validationError}>
                        {validationErrors.checks[`check_${index}`].amount}
                      </div>
                    )}
                  </div>
                </div>

                {checks.length > 1 && (
                  <button
                    type="button"
                    className={styles.removeCheckButton}
                    onClick={() => {
                      const newChecks = checks.filter((_, i) => i !== index);
                      setChecks(newChecks);
                    }}
                    title="מחק צ'ק"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Additional Fields Section */}
        <div className={styles.formSection}>
          <div className={styles.formRow}>
            {/* Paid To Field */}
            <div className={styles.formGroup}>
              <label htmlFor="paidTo">תשלום ל-:</label>
              <input
                id="paidTo"
                type="text"
                name="paidTo"
                value={currentExpense.paidTo || ''}
                onChange={onExpenseInputChange}
                placeholder="תשלום ל"
              />
            </div>

            {/* Vendor Field */}
            <div className={styles.formGroup}>
              <label htmlFor="vendor">שם ספק:</label>
              <input
                id="vendor"
                type="text"
                name="vendor"
                value={currentExpense.vendor || ''}
                onChange={onExpenseInputChange}
                placeholder="שם הספק (אופציונלי)"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            {/* Invoice Number Field */}
            <div className={styles.formGroup}>
              <label htmlFor="invoiceNumber">מספר חשבונית:</label>
              <input
                id="invoiceNumber"
                type="text"
                name="invoiceNumber"
                value={currentExpense.invoiceNumber || ''}
                onChange={onExpenseInputChange}
                placeholder="מספר חשבונית (אופציונלי)"
              />
            </div>
            <div className={styles.formGroup}>
              {/* Empty div for grid layout */}
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className={styles.formSection}>
          <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
            העלאת קובץ/תמונה:
          </h4>

          <div className={styles.fileUploadContainer}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={onFileUpload}
              accept="image/*,.pdf"
              style={{ display: 'none' }}
            />

            <input
              type="file"
              ref={cameraInputRef}
              onChange={onCameraCapture}
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
            />

            <div className={styles.uploadButtons}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={styles.uploadButton}
                disabled={isUploading}
              >
                <Upload size={16} />
                בחר קובץ
              </button>

              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className={styles.cameraButton}
                disabled={isUploading}
              >
                <Camera size={16} />
                צלם תמונה
              </button>
            </div>

            {isUploading && (
              <div className={styles.uploadStatus}>
                <span className={styles.spinner}>⏳</span>
                מעלה קובץ לשרת...
              </div>
            )}

            {currentExpense.uploadedFile && (
              <div className={styles.uploadedFileInfo}>
                <div className={styles.fileDetails}>
                  <span className={styles.fileName}>📎 {currentExpense.uploadedFile.name}</span>
                  <span className={styles.fileSize}>({formatFileSize(currentExpense.uploadedFile.size)})</span>
                </div>
                <button
                  type="button"
                  onClick={onRemoveUploadedFile}
                  className={styles.removeFileButton}
                  title="הסר קובץ"
                >
                  <Trash size={16} />
                </button>
              </div>
            )}

            {currentExpense.receiptImageURL && !currentExpense.uploadedFile && (
              <div className={styles.existingFilePreview}>
                <div
                  className={styles.filePreviewIcon}
                  onClick={() => onViewInvoice && onViewInvoice(currentExpense.receiptImageURL)}
                  title="לחץ לצפייה בקובץ"
                >
                  <Eye size={24} />
                  <span>קובץ קיים</span>
                </div>
              </div>
            )}
          </div>

          {/* Invoice Image Preview */}
          {currentExpense.invoiceImage && (
            <div className={styles.imagePreviewSection}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                תמונת חשבונית:
              </h4>
              <div className={styles.invoiceImageContainer}>
                <img
                  src={currentExpense.invoiceImage}
                  alt="תמונת חשבונית"
                  className={styles.invoiceImagePreview}
                />
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          {/* Hide cancel button on mobile (will be rendered via portal) */}
          {!isMobile && (
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onCancel}
              disabled={loading}
            >
              ביטול
            </button>
          )}
          {/* Hide update button on mobile when editing (will be rendered via portal) */}
          {!(isMobile && isEditing) && (
            <button
              type="submit"
              className={`${styles.saveButton} ${isEditing ? styles.updateButton : ''}`}
              disabled={loading}
            >
              {loading ? 'שומר...' : isEditing ? (
                <>
                  <Check size={20} />
                  <span className={styles.buttonTextDesktop}>עדכן הוצאה</span>
                </>
              ) : 'הוסף הוצאה'}
            </button>
          )}
        </div>
      </form>
    </div>
    {mobileCancelButton}
    {mobileUpdateButton}
    </>
  );
};

export default ExpenseForm;
